import type {
  CurrentStep,
  HtmlFinalStep,
  HtmlStepperStep,
} from '@superfunnel/types';
import {
  BaseEdge,
  Controls,
  Edge,
  EdgeProps,
  Handle,
  MiniMap,
  Node,
  NodeMouseHandler,
  Position,
  ReactFlow,
  ReactFlowInstance,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useCallback, useEffect, useState } from 'react';
import { classNames } from '../../utils';
import { useHtmlStepperEditor } from './HtmlStepperEditorContext';
import { StepEditPopover } from './StepEditPopover';
import { FinalStepThumbnail, StepThumbnail } from './StepThumbnail';

// Layout constants
const NODE_WIDTH = 240;
const NODE_HEIGHT_BASE = 220; // Base node height (header + thumbnail)
const BRANCH_ROW_HEIGHT = 28; // Height per branch row
const HORIZONTAL_GAP = 100; // Gap between nodes horizontally
const VERTICAL_GAP = 20; // Gap between branch rows
const EDGE_OFFSET = 30; // Pixels after source node before edge bends
const EDGE_RADIUS = 8; // Border radius for edge bends
const FLOW_THUMBNAIL_HEIGHT = 130; // Height for thumbnails in flow nodes

/** Custom edge that bends a fixed number of pixels after the source node
 *  instead of at the midpoint. Creates clean L-shaped or Z-shaped paths. */
const FixedOffsetEdge = ({
  sourceX,
  sourceY,
  targetX,
  targetY,
  style,
  label,
  labelStyle,
  labelBgStyle,
  labelBgBorderRadius,
}: EdgeProps) => {
  const sameRow = sourceY === targetY;

  let path: string;
  let labelX: number;
  let labelY: number;

  if (sameRow) {
    // Straight horizontal line
    path = `M ${sourceX} ${sourceY} L ${targetX} ${targetY}`;
    labelX = (sourceX + targetX) / 2;
    labelY = sourceY;
  } else {
    // Z-shaped path: right from source → turn after EDGE_OFFSET → vertical → turn into target
    const bendX = sourceX + EDGE_OFFSET;
    const enterX = targetX - EDGE_OFFSET;
    const r = EDGE_RADIUS;
    const goingDown = targetY > sourceY;
    const rY = goingDown ? r : -r;

    if (enterX > bendX + 2 * r) {
      // Enough horizontal room for two bends
      path =
        `M ${sourceX} ${sourceY}` +
        ` L ${bendX} ${sourceY}` +
        ` Q ${bendX + r} ${sourceY}, ${bendX + r} ${sourceY + rY}` +
        ` L ${bendX + r} ${targetY - rY}` +
        ` Q ${bendX + r} ${targetY}, ${bendX + 2 * r} ${targetY}` +
        ` L ${targetX} ${targetY}`;
    } else {
      // Tight space — single midpoint bend
      const midY = (sourceY + targetY) / 2;
      path =
        `M ${sourceX} ${sourceY}` +
        ` L ${bendX} ${sourceY}` +
        ` Q ${bendX + r} ${sourceY}, ${bendX + r} ${sourceY + rY}` +
        ` L ${bendX + r} ${midY - rY}` +
        ` Q ${bendX + r} ${midY}, ${bendX} ${midY}` +
        ` L ${enterX} ${midY}` +
        ` Q ${enterX - r} ${midY}, ${enterX - r} ${midY + rY}` +
        ` L ${enterX - r} ${targetY - rY}` +
        ` Q ${enterX - r} ${targetY}, ${enterX} ${targetY}` +
        ` L ${targetX} ${targetY}`;
    }
    labelX = bendX + r;
    labelY = (sourceY + targetY) / 2;
  }

  return (
    <>
      <BaseEdge path={path} style={style} />
      {label && (
        <foreignObject
          x={labelX - 60}
          y={labelY - 12}
          width={120}
          height={24}
          style={{ overflow: 'visible' }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                padding: '4px 6px',
                borderRadius: (labelBgBorderRadius as number) ?? 6,
                fontSize: 11,
                fontWeight: 600,
                color:
                  ((labelStyle as Record<string, unknown>)?.fill as string) ??
                  '#3b82f6',
                background:
                  ((labelBgStyle as Record<string, unknown>)?.fill as string) ??
                  '#eff6ff',
                border: `1px solid ${(labelBgStyle as Record<string, unknown>)?.stroke ?? '#bfdbfe'}`,
                whiteSpace: 'nowrap',
              }}
            >
              {label as string}
            </div>
          </div>
        </foreignObject>
      )}
    </>
  );
};

// Custom edge with angle control
// exitAngle: angle in degrees (0 = right, 90 = down, -90 = up, 180 = left)
// curveIntensity: multiplier for control point distance (default 0.5, higher = taller arc)
const AngledEdge = ({
  sourceX,
  sourceY,
  targetX,
  targetY,
  style,
  label,
  data,
}: EdgeProps & {
  data?: { exitAngle?: number; entryAngle?: number; curveIntensity?: number };
}) => {
  const exitAngle = data?.exitAngle ?? 0; // Default: exit to the right
  const entryAngle = data?.entryAngle ?? 180; // Default: enter from the left
  const curveIntensity = data?.curveIntensity ?? 0.5; // Default: standard curve

  // Convert angle to radians
  const exitRad = (exitAngle * Math.PI) / 180;
  const entryRad = (entryAngle * Math.PI) / 180;

  // Calculate distance for control points (affects curve height)
  const distance = Math.sqrt(
    Math.pow(targetX - sourceX, 2) + Math.pow(targetY - sourceY, 2),
  );
  const controlDistance = Math.min(distance * curveIntensity, 200);

  // Calculate control points based on angles
  const cp1x = sourceX + Math.cos(exitRad) * controlDistance;
  const cp1y = sourceY + Math.sin(exitRad) * controlDistance;
  const cp2x = targetX + Math.cos(entryRad) * controlDistance;
  const cp2y = targetY + Math.sin(entryRad) * controlDistance;

  // Create cubic bezier path
  const path = `M ${sourceX} ${sourceY} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${targetX} ${targetY}`;

  // Calculate label position at the actual midpoint of the bezier curve (t=0.5)
  // Cubic bezier formula: P(t) = (1-t)³P0 + 3(1-t)²tP1 + 3(1-t)t²P2 + t³P3
  const t = 0.5;
  const mt = 1 - t;
  const labelX =
    mt * mt * mt * sourceX +
    3 * mt * mt * t * cp1x +
    3 * mt * t * t * cp2x +
    t * t * t * targetX;
  const labelY =
    mt * mt * mt * sourceY +
    3 * mt * mt * t * cp1y +
    3 * mt * t * t * cp2y +
    t * t * t * targetY;

  return (
    <>
      <BaseEdge path={path} style={style} />
      {label && (
        <g>
          <rect
            x={labelX - 30}
            y={labelY - 10}
            width={60}
            height={20}
            rx={10}
            ry={10}
            fill="white"
            stroke="#e4e7ec"
            strokeWidth={1}
          />
          <text
            x={labelX}
            y={labelY}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{ fontSize: 11, fill: '#555', fontWeight: 500 }}
            className="react-flow__edge-text"
          >
            {label}
          </text>
        </g>
      )}
    </>
  );
};

const edgeTypes = { angled: AngledEdge, fixedOffset: FixedOffsetEdge };

// Color palette for condition branches — ordered for max contrast (color-blind friendly)
const BRANCH_COLORS = [
  {
    stroke: '#3b82f6',
    text: '#2563eb',
    bg: '#eff6ff',
    border: '#bfdbfe',
    selectedBg: '#dbeafe',
    selectedText: '#1e40af',
  }, // blue
  {
    stroke: '#f59e0b',
    text: '#d97706',
    bg: '#fffbeb',
    border: '#fde68a',
    selectedBg: '#fef3c7',
    selectedText: '#92400e',
  }, // amber
  {
    stroke: '#10b981',
    text: '#059669',
    bg: '#ecfdf5',
    border: '#a7f3d0',
    selectedBg: '#d1fae5',
    selectedText: '#065f46',
  }, // emerald
  {
    stroke: '#ec4899',
    text: '#db2777',
    bg: '#fdf2f8',
    border: '#fbcfe8',
    selectedBg: '#fce7f3',
    selectedText: '#9d174d',
  }, // pink
  {
    stroke: '#06b6d4',
    text: '#0891b2',
    bg: '#ecfeff',
    border: '#a5f3fc',
    selectedBg: '#cffafe',
    selectedText: '#155e75',
  }, // cyan
  {
    stroke: '#f43f5e',
    text: '#e11d48',
    bg: '#fff1f2',
    border: '#fecdd3',
    selectedBg: '#ffe4e6',
    selectedText: '#9f1239',
  }, // rose
  {
    stroke: '#8b5cf6',
    text: '#7c3aed',
    bg: '#f5f3ff',
    border: '#ddd6fe',
    selectedBg: '#ede9fe',
    selectedText: '#5b21b6',
  }, // violet
  {
    stroke: '#6366f1',
    text: '#4f46e5',
    bg: '#eef2ff',
    border: '#c7d2fe',
    selectedBg: '#e0e7ff',
    selectedText: '#3730a3',
  }, // indigo
];

const getBranchColor = (index: number) =>
  BRANCH_COLORS[index % BRANCH_COLORS.length];

// Custom node component for steps
type BranchInfo = {
  handleId: string;
  label: string;
  defaultValue: string;
  type: 'condition' | 'default';
  color?: (typeof BRANCH_COLORS)[number];
};

const StepNode = ({
  data,
}: {
  data: {
    label: string;
    index: number;
    step: HtmlStepperStep | HtmlFinalStep;
    hideTargetHandle?: boolean;
    hideSourceHandle?: boolean;
    sourcePosition?: Position;
    targetPosition?: Position;
    isSelected?: boolean;
    isFinalStep?: boolean;
    isPopoverOpen?: boolean;
    onPopoverOpenChange?: (open: boolean) => void;
    branches?: BranchInfo[];
  };
}) => {
  const {
    hideTargetHandle,
    hideSourceHandle,
    label,
    index,
    step,
    sourcePosition = Position.Right,
    targetPosition = Position.Left,
    isSelected = false,
    isFinalStep = false,
    isPopoverOpen = false,
    onPopoverOpenChange,
    branches = [],
  } = data;

  // Get step name/description based on step type
  const stepDescription =
    'stepType' in step
      ? step.stepType === 'redirect'
        ? (step as { url: string }).url || 'Redirect URL'
        : (step as { name?: string }).name || 'Custom HTML'
      : (step as HtmlStepperStep).name ||
        ('conditions' in step && step.conditions?.length
          ? `${step.conditions.length} condition(s)`
          : 'No conditions');

  const nodeContent = (
    <div
      className={classNames(
        'flex w-60 cursor-pointer flex-col gap-2 rounded-xl border p-3 transition-all duration-150',
        isSelected
          ? 'border-[#795DFC] bg-white ring-2 ring-[#795DFC]/20'
          : 'border-[#e4e7ec] bg-white hover:border-[#c4b5fd]',
      )}
    >
      {!hideTargetHandle && (
        <Handle
          type="target"
          position={targetPosition}
          className="h-2! w-2! border-none! bg-[#795DFC]!"
        />
      )}
      {/* Header row with number and text */}
      <div className="flex items-center gap-2">
        <div
          className={classNames(
            'relative flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
            'bg-[#f2f4f7]',
          )}
        >
          <p className={classNames('text-sm font-medium text-[#344051]')}>
            {index + 1}
          </p>
        </div>
        <div className="flex min-w-0 grow flex-col items-start justify-start gap-0.5">
          <p className="text-xs font-medium text-[#141c25]">
            {label} <span className="text-base-400 ms-1">{step.id}</span>
          </p>
          <p className="w-full truncate text-xs text-[#414e62]">
            {stepDescription}
          </p>
        </div>
      </div>
      {/* Thumbnail */}
      {isFinalStep ? (
        <FinalStepThumbnail
          step={step as HtmlFinalStep}
          height={FLOW_THUMBNAIL_HEIGHT}
        />
      ) : (
        <StepThumbnail stepId={step.id} height={FLOW_THUMBNAIL_HEIGHT} />
      )}
      {/* Branch rows with individual source handles */}
      {!hideSourceHandle && branches.length > 0 && (
        <div className="mt-1 flex flex-col gap-1.5">
          {branches.map((branch) => (
            <div
              key={branch.handleId}
              className="relative flex items-center gap-1.5 rounded-md px-2 py-1.5"
              style={{
                backgroundColor:
                  branch.type === 'default'
                    ? '#f8f9fa'
                    : (branch.color?.bg ?? '#eff6ff'),
              }}
            >
              <span
                className="grow truncate text-[10px] leading-tight font-medium"
                style={{
                  color:
                    branch.type === 'default'
                      ? '#64748b'
                      : (branch.color?.text ?? '#2563eb'),
                }}
              >
                {branch.label}{' '}
                <span className="text-base-400 ms-1">
                  {branch.defaultValue}
                </span>
              </span>
              <Handle
                type="source"
                position={sourcePosition}
                id={branch.handleId}
                className="h-1.75! w-1.75! rounded-full! border-[1.5px]! border-white!"
                style={{
                  backgroundColor:
                    branch.type === 'default'
                      ? '#94a3b8'
                      : (branch.color?.stroke ?? '#3b82f6'),
                }}
              />
            </div>
          ))}
        </div>
      )}
      {/* Fallback single source handle when no branches */}
      {!hideSourceHandle && branches.length === 0 && (
        <Handle
          type="source"
          position={sourcePosition}
          className="h-2! w-2! border-none! bg-[#795DFC]!"
        />
      )}
    </div>
  );

  return (
    <StepEditPopover
      open={isPopoverOpen}
      onOpenChange={onPopoverOpenChange}
      type={isFinalStep ? 'finalStep' : 'step'}
      step={!isFinalStep ? (step as HtmlStepperStep) : undefined}
      finalStep={isFinalStep ? (step as HtmlFinalStep) : undefined}
      side="right"
      sideOffset={12}
      align="start"
    >
      {nodeContent}
    </StepEditPopover>
  );
};

const nodeTypes = { step: StepNode };

export const HtmlStepperEditorLogic = () => {
  const { data, state, iframe } = useHtmlStepperEditor();
  const { formData } = data;
  const { editorMode, currentStep, setCurrentStep } = state;

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  // Track which node's popover is open (by node id)
  const [popoverOpenNodeId, setPopoverOpenNodeId] = useState<string | null>(
    null,
  );

  // Handle node click to select step and toggle popover
  const handleNodeClick: NodeMouseHandler<Node> = useCallback(
    (event, node) => {
      if (!formData) return;

      // Check if the click originated from inside a popover (ignore those)
      const target = event.target as HTMLElement;
      if (target.closest('[data-radix-popper-content-wrapper]')) {
        return;
      }

      // Check if it's a final step
      const isFinalStep = formData.finalSteps.some((s) => s.id === node.id);

      const newCurrentStep: CurrentStep = isFinalStep
        ? { type: 'finalStep', id: node.id }
        : { type: 'step', id: node.id };

      // If clicking the same node that's already selected, toggle the popover
      if (popoverOpenNodeId === node.id) {
        setPopoverOpenNodeId(null);
        return;
      }

      setCurrentStep(newCurrentStep);
      iframe.goToStep(newCurrentStep);
      setPopoverOpenNodeId((prev) => (prev === node.id ? null : node.id));
    },
    [formData, iframe, setCurrentStep, popoverOpenNodeId],
  );

  // Create a callback to handle popover close for a specific node
  const createPopoverOpenChangeHandler = useCallback(
    (_nodeId: string) => (open: boolean) => {
      if (!open) {
        setPopoverOpenNodeId(null);
      }
    },
    [],
  );

  const updateNodesAndEdges = useCallback(() => {
    if (!formData) return;

    const newNodes: Node[] = [];
    const newEdges: Edge[] = [];

    // ── Step 1: Build lookup maps ────────────────────────────────────
    const stepIdToIndex = new Map<string, number>();
    formData.steps.forEach((s, i) => stepIdToIndex.set(s.id, i));

    const finalStepIds = new Set(formData.finalSteps.map((s) => s.id));

    // ── Step 2: Resolve ALL conditions per step ──────────────────────
    // Each resolved condition has a target ID and whether it goes to the next step
    type ResolvedCondition = {
      conditionId: string;
      conditionIndex: number;
      targetStepId: string;
      targetIsFinal: boolean;
      targetsNextStep: boolean; // true if target is the immediately next sequential step
      label: string;
      defaultValue: string;
    };

    const resolvedConditionsByStep = new Map<string, ResolvedCondition[]>();

    formData.steps.forEach((step, index) => {
      if (!step.conditions?.length) return;

      // Determine the effective default next step (for resolving empty condition targets)
      const effectiveDefaultNextId =
        step.defaultNextStep ||
        (index < formData.steps.length - 1
          ? formData.steps[index + 1].id
          : undefined);

      const resolved: ResolvedCondition[] = [];
      step.conditions.forEach((condition, condIndex) => {
        let targetId = condition.then.step;

        // Empty string = "Next Step" default → use effective default
        if (!targetId && effectiveDefaultNextId) {
          targetId = effectiveDefaultNextId;
        }
        if (!targetId) return;

        const isFinal = finalStepIds.has(targetId);
        if (!isFinal && !stepIdToIndex.has(targetId)) return; // orphaned

        const isNextStep =
          !isFinal &&
          effectiveDefaultNextId !== undefined &&
          targetId === effectiveDefaultNextId;

        resolved.push({
          conditionId: condition.id,
          conditionIndex: condIndex,
          targetStepId: targetId,
          targetIsFinal: isFinal,
          targetsNextStep: isNextStep,
          label: condition.name || `Branch ${condIndex + 1}`,
          defaultValue: condition.if
            .map((c) => {
              if (condition.name) return '';
              const displayVal = (v: unknown): string =>
                typeof v === 'object' && v !== null
                  ? ((v as { label?: string; value?: string }).label ??
                    (v as { value?: string }).value ??
                    String(v))
                  : String(v);
              const val = Array.isArray(c.value)
                ? `(${c.value.map(displayVal).join(', ')})`
                : `(${displayVal(c.value)})`;
              return val;
            })
            .join(', '),
        });
      });

      if (resolved.length > 0) {
        resolvedConditionsByStep.set(step.id, resolved);
      }
    });

    // ── Step 3: Exhaustive check (conservative) ──────────────────────
    // A step is exhaustive ONLY if:
    //   - All conditions are single-rule (one `if` clause each)
    //   - All rules reference the SAME field
    //   - The operator is `equals` or `isAny`
    //   - The union of values covers ALL options of that field
    const exhaustiveSteps = new Set<string>();

    formData.steps.forEach((step) => {
      if (!step.conditions?.length || !step.fields?.length) return;

      // Check: all conditions have exactly one rule
      const allSingleRule = step.conditions.every((c) => c.if.length === 1);
      if (!allSingleRule) return;

      // Check: all rules reference the same field with equals/isAny
      const rules = step.conditions.map((c) => c.if[0]);
      const fieldId = rules[0].field;
      if (!fieldId) return;
      const allSameField = rules.every(
        (r) =>
          r.field === fieldId &&
          (r.operator === 'equals' || r.operator === 'isAny'),
      );
      if (!allSameField) return;

      // Collect covered values
      const coveredValues = new Set<string>();
      for (const rule of rules) {
        if (rule.operator === 'equals' && rule.value != null) {
          coveredValues.add(String(rule.value));
        } else if (rule.operator === 'isAny' && Array.isArray(rule.value)) {
          rule.value.forEach((v: string) => coveredValues.add(String(v)));
        }
      }

      // Find the field and check coverage
      const field = step.fields.find((f) => f.id === fieldId);
      if (
        field &&
        'options' in field &&
        Array.isArray((field as { options?: { value: string }[] }).options)
      ) {
        const options = (field as { options: { value: string }[] }).options;
        if (
          options.length > 0 &&
          options.every((opt) => coveredValues.has(opt.value))
        ) {
          exhaustiveSteps.add(step.id);
        }
      }
    });

    // ── Step 4: Build navigation graph ─────────────────────────────
    // For each step, compute:
    //   - defaultTarget: where the default route goes (defaultNextStep or steps[i+1])
    //   - conditionTargets: where each condition branch goes (non-final only)
    //   - predecessors: all steps that can navigate TO this step
    const defaultTarget = new Map<string, string>();
    const conditionTargets = new Map<string, string[]>();
    const predecessors = new Map<string, string[]>();

    // Initialize predecessors
    formData.steps.forEach((step) => predecessors.set(step.id, []));

    formData.steps.forEach((step, index) => {
      // Default target
      const dt = step.defaultNextStep;
      if (dt && stepIdToIndex.has(dt)) {
        defaultTarget.set(step.id, dt);
        predecessors.get(dt)!.push(step.id);
      } else if (!dt && index < formData.steps.length - 1) {
        const nextId = formData.steps[index + 1].id;
        defaultTarget.set(step.id, nextId);
        predecessors.get(nextId)!.push(step.id);
      }

      // Condition targets for LAYOUT purposes (non-final, non-duplicate).
      // EXCLUDE targets that match the default target — those are redundant
      // for positioning (they share the default path's node).
      // Only truly DIFFERENT targets get their own branch row.
      const conditions = resolvedConditionsByStep.get(step.id);
      if (conditions) {
        const dt = defaultTarget.get(step.id);
        const targets: string[] = [];
        const seen = new Set<string>();
        conditions.forEach((cond) => {
          if (
            !cond.targetIsFinal &&
            !seen.has(cond.targetStepId) &&
            cond.targetStepId !== dt
          ) {
            seen.add(cond.targetStepId);
            targets.push(cond.targetStepId);
            predecessors.get(cond.targetStepId)?.push(step.id);
          }
        });
        if (targets.length > 0) {
          conditionTargets.set(step.id, targets);
        }
      }
    });

    // ── Step 5: Column assignment (topological) ──────────────────────
    // column(step) = max(column of all predecessors) + 1.
    // Step 0 always gets column 0.
    // Process in BFS order to ensure predecessors are resolved first.
    const stepColumn = new Map<string, number>();
    stepColumn.set(formData.steps[0].id, 0);

    // Iterative relaxation: repeat until stable (handles all DAG shapes)
    let colChanged = true;
    while (colChanged) {
      colChanged = false;
      formData.steps.forEach((step) => {
        const preds = predecessors.get(step.id) ?? [];
        if (preds.length === 0 && step.id !== formData.steps[0].id) {
          // Orphan step (no predecessors): column = array index
          if (!stepColumn.has(step.id)) {
            stepColumn.set(step.id, stepIdToIndex.get(step.id)!);
            colChanged = true;
          }
          return;
        }

        // Only compute once all predecessors have columns
        const allPredsResolved = preds.every((p) => stepColumn.has(p));
        if (!allPredsResolved) return;

        const newCol =
          step.id === formData.steps[0].id
            ? 0
            : Math.max(...preds.map((p) => stepColumn.get(p)!)) + 1;

        if (stepColumn.get(step.id) !== newCol) {
          stepColumn.set(step.id, newCol);
          colChanged = true;
        }
      });
    }

    // ── Step 6: Row assignment ───────────────────────────────────────
    // - Default target → same row as source step
    // - Condition branches → stacked above source (branch 1 = row-1,
    //   branch 2 = row-2, etc. relative to source)
    // - Row spans: when a default connection spans cols A→B on the same
    //   row, ALL intermediate columns are reserved on that row to prevent
    //   unrelated branches from landing in the middle of a chain.
    const stepRow = new Map<string, number>();

    // Process columns left → right; within each column process steps
    // in the order they were assigned.
    const maxColumn = Math.max(...Array.from(stepColumn.values()), 0);
    const usedRows = new Set<string>(); // "col:row" to detect collisions

    const getRowKey = (col: number, row: number) => `${col}:${row}`;

    /** Reserve a row for all columns from startCol to endCol (inclusive) */
    const reserveRowSpan = (startCol: number, endCol: number, row: number) => {
      for (let c = startCol; c <= endCol; c++) {
        usedRows.add(getRowKey(c, row));
      }
    };

    // Find an unused row near the desired row for a given column
    const findFreeRow = (col: number, desiredRow: number): number => {
      if (!usedRows.has(getRowKey(col, desiredRow))) return desiredRow;
      // Search outward: -1, +1, -2, +2, ...
      for (let offset = 1; offset < 100; offset++) {
        if (!usedRows.has(getRowKey(col, desiredRow - offset)))
          return desiredRow - offset;
        if (!usedRows.has(getRowKey(col, desiredRow + offset)))
          return desiredRow + offset;
      }
      return desiredRow;
    };

    // Assign step 0 at row 0
    stepRow.set(formData.steps[0].id, 0);
    usedRows.add(getRowKey(0, 0));

    // Process column by column (left → right)
    for (let col = 0; col <= maxColumn; col++) {
      // Gather all steps in this column, sorted by array index
      const stepsInCol = formData.steps.filter(
        (s) => stepColumn.get(s.id) === col,
      );

      // Now process outgoing from steps in this column.
      // Layout rule:
      //   - If step has NO conditions: default target → same row (straight line)
      //   - If step HAS conditions: all targets fan out:
      //       Branch 1 → farthest row above source (top)
      //       Branch N → just above source
      //       Default  → row below source (bottom)
      for (const step of stepsInCol) {
        if (!stepRow.has(step.id)) continue;
        const sourceRow = stepRow.get(step.id)!;
        const sourceCol = stepColumn.get(step.id)!;

        const branches = conditionTargets.get(step.id);
        const hasBranches = branches && branches.length > 0;

        // Condition targets: stacked above source, first branch at top
        if (branches) {
          branches.forEach((targetId, branchIdx) => {
            if (stepRow.has(targetId)) return;
            const tCol = stepColumn.get(targetId);
            if (tCol === undefined) return;

            const desiredRow = sourceRow - (branches.length - branchIdx);
            const freeRow = findFreeRow(tCol, desiredRow);
            stepRow.set(targetId, freeRow);
            // Reserve the entire span from source to target on this row
            reserveRowSpan(sourceCol, tCol, freeRow);
          });
        }

        // Default target
        const dt = defaultTarget.get(step.id);
        if (dt && !stepRow.has(dt)) {
          const dtCol = stepColumn.get(dt);
          if (dtCol !== undefined) {
            // If this step has condition branches, put default BELOW source
            // Otherwise, keep it on the same row (straight line)
            const desiredRow = hasBranches ? sourceRow + 1 : sourceRow;
            const freeRow = findFreeRow(dtCol, desiredRow);
            stepRow.set(dt, freeRow);
            // Reserve the entire span from source to target on this row
            reserveRowSpan(sourceCol, dtCol, freeRow);
          }
        }
      }
    }

    // Assign any remaining unplaced steps (safety net)
    formData.steps.forEach((step) => {
      if (stepRow.has(step.id)) return;
      const col = stepColumn.get(step.id) ?? 0;
      const freeRow = findFreeRow(col, 0);
      stepRow.set(step.id, freeRow);
      usedRows.add(getRowKey(col, freeRow));
    });

    // ── Step 6b: Build branch info per step & position nodes ────────
    // First pass: compute branch info and node heights for all steps
    const stepBranchInfo = new Map<string, BranchInfo[]>();
    let maxNodeHeight = NODE_HEIGHT_BASE + BRANCH_ROW_HEIGHT; // at least 1 branch (default)

    formData.steps.forEach((step) => {
      const stepBranches: BranchInfo[] = [];
      const conditions = resolvedConditionsByStep.get(step.id);
      if (conditions) {
        conditions.forEach((cond, condIdx) => {
          stepBranches.push({
            handleId: `condition-${cond.conditionId}`,
            label: cond.label,
            defaultValue: cond.defaultValue,
            type: 'condition',
            color: getBranchColor(condIdx),
          });
        });
      }
      // Always add default row
      stepBranches.push({
        handleId: 'default',
        label: 'Default',
        type: 'default',
        defaultValue: '',
      });
      stepBranchInfo.set(step.id, stepBranches);

      const nodeHeight =
        NODE_HEIGHT_BASE + stepBranches.length * BRANCH_ROW_HEIGHT;
      if (nodeHeight > maxNodeHeight) maxNodeHeight = nodeHeight;
    });

    // Use the tallest node height as the uniform row height
    const ROW_HEIGHT = maxNodeHeight + VERTICAL_GAP;

    // Second pass: position nodes
    formData.steps.forEach((step, index) => {
      const row = stepRow.get(step.id) ?? 0;
      const col = stepColumn.get(step.id) ?? index;

      const x = col * (NODE_WIDTH + HORIZONTAL_GAP);
      const y = row * ROW_HEIGHT;
      const isSelected =
        currentStep?.type === 'step' && currentStep.id === step.id;

      newNodes.push({
        id: step.id,
        type: 'step',
        data: {
          label: 'Step ' + (index + 1),
          index,
          step,
          hideTargetHandle: index === 0,
          sourcePosition: Position.Right,
          targetPosition: Position.Left,
          isSelected,
          isFinalStep: false,
          isPopoverOpen: popoverOpenNodeId === step.id,
          onPopoverOpenChange: createPopoverOpenChangeHandler(step.id),
          branches: stepBranchInfo.get(step.id) ?? [],
        },
        position: { x, y },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
      });
    });

    // ── Final steps: vertical column at the end ────────────────────
    // Place each final step on the row of its primary source step so
    // edges stay horizontal. The first final step = default destination
    // for the last regular step.
    const FINAL_STEP_GAP = HORIZONTAL_GAP + 40;
    const finalStepCol = maxColumn + 1;
    const finalStepsX =
      finalStepCol * (NODE_WIDTH + HORIZONTAL_GAP) -
      HORIZONTAL_GAP +
      FINAL_STEP_GAP;

    // Build: finalStepId → [sourceStepIds] from conditions + defaultNextStep
    const finalStepSources = new Map<string, string[]>();
    formData.finalSteps.forEach((fs) => finalStepSources.set(fs.id, []));

    formData.steps.forEach((step) => {
      // Check defaultNextStep
      if (step.defaultNextStep && finalStepIds.has(step.defaultNextStep)) {
        finalStepSources.get(step.defaultNextStep)!.push(step.id);
      }
      // Check condition targets
      const conditions = resolvedConditionsByStep.get(step.id);
      if (conditions) {
        conditions.forEach((cond) => {
          if (cond.targetIsFinal) {
            finalStepSources.get(cond.targetStepId)?.push(step.id);
          }
        });
      }
    });

    // The last regular step implicitly routes to finalSteps[0]
    const lastStep = formData.steps[formData.steps.length - 1];
    if (lastStep && formData.finalSteps[0]) {
      const defaultFinalSources = finalStepSources.get(
        formData.finalSteps[0].id,
      )!;
      if (!defaultFinalSources.includes(lastStep.id)) {
        defaultFinalSources.push(lastStep.id);
      }
    }

    // Assign y-positions to final steps.
    // Final steps have no branches, so use compact spacing when resolving
    // collisions instead of the full ROW_HEIGHT used for regular steps.
    const FINAL_NODE_HEIGHT = NODE_HEIGHT_BASE + BRANCH_ROW_HEIGHT; // just 1 default row
    const FINAL_SPACING = FINAL_NODE_HEIGHT + VERTICAL_GAP;
    const placedFinalYs: number[] = [];

    formData.finalSteps.forEach((step, index) => {
      const sources = finalStepSources.get(step.id) ?? [];

      let desiredY: number;
      if (sources.length > 0) {
        // Align to the row of the first source step for horizontal edges
        const sourceRows = sources
          .map((sid) => stepRow.get(sid))
          .filter((r): r is number => r !== undefined);
        desiredY =
          sourceRows.length > 0
            ? sourceRows[0] * ROW_HEIGHT
            : index * FINAL_SPACING;
      } else {
        desiredY = index * FINAL_SPACING;
      }

      // Avoid overlaps with already-placed final steps using compact spacing
      for (const usedY of placedFinalYs) {
        if (Math.abs(desiredY - usedY) < FINAL_SPACING) {
          desiredY = usedY + FINAL_SPACING;
        }
      }
      placedFinalYs.push(desiredY);

      const isSelected =
        currentStep?.type === 'finalStep' && currentStep.id === step.id;

      newNodes.push({
        id: step.id,
        type: 'step',
        data: {
          label: 'Final Step ' + (index + 1),
          index,
          step,
          hideTargetHandle: false,
          hideSourceHandle: true,
          sourcePosition: Position.Right,
          targetPosition: Position.Left,
          isSelected,
          isFinalStep: true,
          isPopoverOpen: popoverOpenNodeId === step.id,
          onPopoverOpenChange: createPopoverOpenChangeHandler(step.id),
        },
        position: {
          x: finalStepsX,
          y: desiredY,
        },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
      });
    });

    // ── Step 7: Default flow edges ───────────────────────────────────
    // Connect step[i] → its default target (defaultNextStep or step[i+1]).
    // Always drawn (even for exhaustive steps) so the sequential chain
    // stays visually connected.
    formData.steps.forEach((step, index) => {
      // Determine default target: explicit defaultNextStep or next sequential
      const stepDefaultTarget = step.defaultNextStep;
      let targetId: string | undefined;

      if (stepDefaultTarget) {
        // Validate the target exists (regular step or final step)
        if (
          stepIdToIndex.has(stepDefaultTarget) ||
          finalStepIds.has(stepDefaultTarget)
        ) {
          targetId = stepDefaultTarget;
        }
      } else if (index < formData.steps.length - 1) {
        targetId = formData.steps[index + 1].id;
      }

      if (!targetId) return;

      newEdges.push({
        id: `default-edge-${step.id}-${targetId}`,
        source: step.id,
        sourceHandle: 'default',
        target: targetId,
        animated: false,
        type: 'fixedOffset',
        zIndex: 10,
        style: { stroke: '#94a3b8', strokeDasharray: '5,5', opacity: 0.55 },
      });
    });

    // ── Step 8: Branch edges ─────────────────────────────────────────
    formData.steps.forEach((step, index) => {
      const conditions = resolvedConditionsByStep.get(step.id);
      const hasDefaultNextStep = !!step.defaultNextStep;

      if (!conditions) {
        // No conditions — if last step and no defaultNextStep, connect to first final step
        if (index === formData.steps.length - 1 && !hasDefaultNextStep) {
          const finalStep = formData.finalSteps[0];
          if (finalStep) {
            newEdges.push({
              id: `default-edge-${step.id}-${finalStep.id}`,
              source: step.id,
              sourceHandle: 'default',
              target: finalStep.id,
              animated: false,
              type: 'fixedOffset',
              zIndex: 10,
              style: {
                stroke: '#94a3b8',
                strokeDasharray: '5,5',
                opacity: 0.55,
              },
            });
          }
        }
        return;
      }

      const isExhaustive = exhaustiveSteps.has(step.id);

      conditions.forEach((cond, condIdx) => {
        const edgeColor = getBranchColor(condIdx);
        newEdges.push({
          id: `condition-${step.id}-${cond.conditionId}-${cond.targetStepId}`,
          source: step.id,
          sourceHandle: `condition-${cond.conditionId}`,
          target: cond.targetStepId,
          animated: false,
          type: 'fixedOffset',
          zIndex: 10,
          style: { stroke: edgeColor.stroke, strokeWidth: 2, opacity: 0.55 },
        });
      });

      // If last step, not exhaustive, and no defaultNextStep, connect to first final step
      if (
        index === formData.steps.length - 1 &&
        !isExhaustive &&
        !hasDefaultNextStep
      ) {
        const finalStep = formData.finalSteps[0];
        if (finalStep) {
          newEdges.push({
            id: `default-edge-${step.id}-${finalStep.id}`,
            source: step.id,
            sourceHandle: 'default',
            target: finalStep.id,
            animated: false,
            type: 'fixedOffset',
            zIndex: 10,
            style: { stroke: '#94a3b8', strokeDasharray: '5,5', opacity: 0.55 },
          });
        }
      }
    });

    setNodes(newNodes);
    setEdges(newEdges);
  }, [
    formData,
    currentStep,
    popoverOpenNodeId,
    createPopoverOpenChangeHandler,
    setNodes,
    setEdges,
  ]);

  useEffect(() => {
    if (formData) {
      updateNodesAndEdges();
    }
  }, [formData, updateNodesAndEdges]);

  // When the flow initialises, focus the selected step (or fall back to
  // panning so the first step sits near the left edge).
  const selectedNodeId =
    currentStep && currentStep.type !== 'wrapper' ? currentStep.id : undefined;

  const handleInit = useCallback(
    (instance: ReactFlowInstance) => {
      // Always fit the full graph first for a consistent zoom level
      instance
        .fitView({
          padding: 0.15,
          minZoom: 0.5,
          maxZoom: 1,
        })
        .then(() => {
          const { zoom } = instance.getViewport();
          if (selectedNodeId) {
            // Pan so the selected node is at the left edge
            const node = instance.getNode(selectedNodeId);
            if (node) {
              const container = document
                .querySelector('.react-flow')
                ?.getBoundingClientRect() ?? { width: 800, height: 600 };
              const nodeX = node.position.x * zoom;
              const nodeY = node.position.y * zoom;
              const nodeH = (node.measured?.height ?? 200) * zoom;
              instance.setViewport({
                x: -nodeX + 30,
                y: -nodeY + container.height / 2 - nodeH / 2,
                zoom,
              });
              return;
            }
          }
          // No selection — align to the left edge
          const { y } = instance.getViewport();
          instance.setViewport({ x: 30, y, zoom });
        });
    },
    [selectedNodeId],
  );

  // Only show in logic mode
  if (editorMode !== 'logic') {
    return null;
  }

  return (
    <div className="relative h-full w-full overflow-hidden bg-white">
      <div className="absolute inset-0 px-2">
        <div className="h-full w-full overflow-hidden rounded-tl-2xl rounded-tr-2xl border-0 bg-[#f8fafc]">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={handleNodeClick}
            // disable user‐created connections
            onConnect={() => {}}
            nodesConnectable={false}
            // keep draggable nodes
            nodesDraggable={true}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onInit={handleInit}
            attributionPosition="bottom-right"
            style={{ backgroundColor: '#f8fafc' }}
            minZoom={0.05}
            defaultViewport={{ x: 0, y: 0, zoom: 1 }}
          >
            {/* <Background /> */}
            <Controls />
            <MiniMap />
          </ReactFlow>
        </div>
      </div>
    </div>
  );
};
